import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// -- FR ------------------------------------------------------------------
import frCommon from './locales/fr/common.json';
import frSidebar from './locales/fr/sidebar.json';
import frHeader from './locales/fr/header.json';
import frDashboard from './locales/fr/dashboard.json';
import frBudget from './locales/fr/budget.json';
import frPert from './locales/fr/pert.json';
import frGantt from './locales/fr/gantt.json';
import frKanban from './locales/fr/kanban.json';
import frRaci from './locales/fr/raci.json';
import frSwot from './locales/fr/swot.json';
import frRisk from './locales/fr/risk.json';
import frTriangle from './locales/fr/triangle.json';
import frWbs from './locales/fr/wbs.json';
import frClosure from './locales/fr/closure.json';
import frSettings from './locales/fr/settings.json';
import frSupport from './locales/fr/support.json';
import frClients from './locales/fr/clients.json';
import frSpecs from './locales/fr/specs.json';
import frDialogs from './locales/fr/dialogs.json';
import frExports from './locales/fr/exports.json';
import frAdmin from './locales/fr/admin.json';
import frAlerts from './locales/fr/alerts.json';

// -- EN ------------------------------------------------------------------
import enCommon from './locales/en/common.json';
import enSidebar from './locales/en/sidebar.json';
import enHeader from './locales/en/header.json';
import enDashboard from './locales/en/dashboard.json';
import enBudget from './locales/en/budget.json';
import enPert from './locales/en/pert.json';
import enGantt from './locales/en/gantt.json';
import enKanban from './locales/en/kanban.json';
import enRaci from './locales/en/raci.json';
import enSwot from './locales/en/swot.json';
import enRisk from './locales/en/risk.json';
import enTriangle from './locales/en/triangle.json';
import enWbs from './locales/en/wbs.json';
import enClosure from './locales/en/closure.json';
import enSettings from './locales/en/settings.json';
import enSupport from './locales/en/support.json';
import enClients from './locales/en/clients.json';
import enSpecs from './locales/en/specs.json';
import enDialogs from './locales/en/dialogs.json';
import enExports from './locales/en/exports.json';
import enAdmin from './locales/en/admin.json';
import enAlerts from './locales/en/alerts.json';

// -- ES ------------------------------------------------------------------
import esCommon from './locales/es/common.json';
import esSidebar from './locales/es/sidebar.json';
import esHeader from './locales/es/header.json';
import esDashboard from './locales/es/dashboard.json';
import esBudget from './locales/es/budget.json';
import esPert from './locales/es/pert.json';
import esGantt from './locales/es/gantt.json';
import esKanban from './locales/es/kanban.json';
import esRaci from './locales/es/raci.json';
import esSwot from './locales/es/swot.json';
import esRisk from './locales/es/risk.json';
import esTriangle from './locales/es/triangle.json';
import esWbs from './locales/es/wbs.json';
import esClosure from './locales/es/closure.json';
import esSettings from './locales/es/settings.json';
import esSupport from './locales/es/support.json';
import esClients from './locales/es/clients.json';
import esSpecs from './locales/es/specs.json';
import esDialogs from './locales/es/dialogs.json';
import esExports from './locales/es/exports.json';
import esAdmin from './locales/es/admin.json';
import esAlerts from './locales/es/alerts.json';

// -- AR ------------------------------------------------------------------
import arCommon from './locales/ar/common.json';
import arSidebar from './locales/ar/sidebar.json';
import arHeader from './locales/ar/header.json';
import arDashboard from './locales/ar/dashboard.json';
import arBudget from './locales/ar/budget.json';
import arPert from './locales/ar/pert.json';
import arGantt from './locales/ar/gantt.json';
import arKanban from './locales/ar/kanban.json';
import arRaci from './locales/ar/raci.json';
import arSwot from './locales/ar/swot.json';
import arRisk from './locales/ar/risk.json';
import arTriangle from './locales/ar/triangle.json';
import arWbs from './locales/ar/wbs.json';
import arClosure from './locales/ar/closure.json';
import arSettings from './locales/ar/settings.json';
import arSupport from './locales/ar/support.json';
import arClients from './locales/ar/clients.json';
import arSpecs from './locales/ar/specs.json';
import arDialogs from './locales/ar/dialogs.json';
import arExports from './locales/ar/exports.json';
import arAdmin from './locales/ar/admin.json';
import arAlerts from './locales/ar/alerts.json';

export const supportedLanguages = ['fr', 'en', 'es', 'ar'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

export const languageNames: Record<SupportedLanguage, string> = {
  fr: 'Français',
  en: 'English',
  es: 'Español',
  ar: 'العربية',
};

export const rtlLanguages: SupportedLanguage[] = ['ar'];

export const isRtl = (lang: string): boolean =>
  rtlLanguages.includes(lang as SupportedLanguage);

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: { common: frCommon, sidebar: frSidebar, header: frHeader, dashboard: frDashboard, budget: frBudget, pert: frPert, gantt: frGantt, kanban: frKanban, raci: frRaci, swot: frSwot, risk: frRisk, triangle: frTriangle, wbs: frWbs, closure: frClosure, settings: frSettings, support: frSupport, clients: frClients, specs: frSpecs, dialogs: frDialogs, exports: frExports, admin: frAdmin, alerts: frAlerts },
      en: { common: enCommon, sidebar: enSidebar, header: enHeader, dashboard: enDashboard, budget: enBudget, pert: enPert, gantt: enGantt, kanban: enKanban, raci: enRaci, swot: enSwot, risk: enRisk, triangle: enTriangle, wbs: enWbs, closure: enClosure, settings: enSettings, support: enSupport, clients: enClients, specs: enSpecs, dialogs: enDialogs, exports: enExports, admin: enAdmin, alerts: enAlerts },
      es: { common: esCommon, sidebar: esSidebar, header: esHeader, dashboard: esDashboard, budget: esBudget, pert: esPert, gantt: esGantt, kanban: esKanban, raci: esRaci, swot: esSwot, risk: esRisk, triangle: esTriangle, wbs: esWbs, closure: esClosure, settings: esSettings, support: esSupport, clients: esClients, specs: esSpecs, dialogs: esDialogs, exports: esExports, admin: esAdmin, alerts: esAlerts },
      ar: { common: arCommon, sidebar: arSidebar, header: arHeader, dashboard: arDashboard, budget: arBudget, pert: arPert, gantt: arGantt, kanban: arKanban, raci: arRaci, swot: arSwot, risk: arRisk, triangle: arTriangle, wbs: arWbs, closure: arClosure, settings: arSettings, support: arSupport, clients: arClients, specs: arSpecs, dialogs: arDialogs, exports: arExports, admin: arAdmin, alerts: arAlerts },
    },
    fallbackLng: 'fr',
    defaultNS: 'common',
    ns: ['common', 'sidebar', 'header', 'dashboard', 'budget', 'pert', 'gantt', 'kanban', 'raci', 'swot', 'risk', 'triangle', 'wbs', 'closure', 'settings', 'support', 'clients', 'specs', 'dialogs', 'exports', 'admin', 'alerts'],
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'archiplan-lang',
    },
  });

// Set initial dir/lang on the document element
i18n.on('initialized', () => {
  const lang = i18n.language ?? 'fr';
  document.documentElement.lang = lang;
  document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
});

i18n.on('languageChanged', (lang: string) => {
  document.documentElement.lang = lang;
  document.documentElement.dir = isRtl(lang) ? 'rtl' : 'ltr';
});

export default i18n;
